import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgToastTranslocoService } from 'src/app/common/services/ng-toast-transloco/ng-toast-transloco.service';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { LoginDetailsService } from '../../services/login-details.service';
import { LoaderService } from 'src/app/common/services/loader/loader.service';
import { emailPattern } from 'src/app/const/constant';
// import { emailPattern } from 'src/app/const/constant';
import countries from 'src/resources/countries.json';

export class SignUpStatus {
  isSuccess: boolean;
  email: String;

  constructor(status: boolean, loginEmail: String) {
    this.isSuccess = status;
    this.email = loginEmail;
  }
}

@Component({
  selector: 'ds-signup',
  // standalone: true,
  // imports: [],
  templateUrl: './signup.component.html',
  styleUrl: './signup.component.scss',
})
export class SignupComponent implements OnInit, OnChanges, AfterViewInit {
  @Output() goToSignin = new EventEmitter();
  @Output() onLogout = new EventEmitter();
  @Output() onSignupCompleted = new EventEmitter<SignUpStatus>();

  registrationForm: FormGroup;
  otpForm: FormGroup;

  otpSent: boolean = false;
  otpCode: any;
  redirectToLogin: boolean = false;

  @Input() showSignUpScreen: boolean;
  @Input() showOTPScreen: boolean;
  @Input() emailAddress: string;
  @Input() emailEntered: string;
  @Input() passwordEntered: string;
  @Input() skipAutoLogin: boolean = false;
  @Input() showMfaSetupDirectly: boolean = false;
  @ViewChild('otp1') otp1: any;
  @ViewChild('otp2') otp2: any;
  @ViewChild('otp3') otp3: any;
  @ViewChild('otp4') otp4: any;
  @ViewChild('otp5') otp5: any;
  @ViewChild('otp6') otp6: any;
  mismatch: boolean;

  // MFA setup state (shown after OTP verification when showMfaSetupOnRegistration is true)
  showMfaSetupScreen: boolean = false;
  showMfaMobileScreen: boolean = false;
  showMfaMobileOTPScreen: boolean = false;
  showMfaAuthenticatorScreen: boolean = false;
  mfaTotpSecret: string = '';
  countries = countries;

  mfaMobileForm: FormGroup;
  mfaOtpForm: FormGroup;
  mfaTotpForm: FormGroup;
  get email() {
    return this.registrationForm.get('email');
  }

  get password() {
    return this.registrationForm.get('password')!;
  }

  get confirmPassword() {
    return this.registrationForm.get('confirmPassword');
  }

  get maskedEmailAddress(): string {
    return this.maskEmail(this.emailAddress);
  }

  get maskedEmailEntered(): string {
    return this.maskEmail(this.emailEntered);
  }

  private maskEmail(username: string): string {
    if (!username || username.trim() === '' || !username.includes('@')) {
      return username;
    }

    try {
      const atIndex = username.indexOf('@');
      if (atIndex <= 2) {
        return username; // Not enough characters to mask
      }

      const maskedPart = '*'.repeat(atIndex - 2);
      const result =
        username.substring(0, 1) + maskedPart + username.substring(atIndex - 1);
      return result;
    } catch (e) {
      this.toast.toastErrorMsg(e);
      return username;
    }
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private toast: NgToastTranslocoService,
    private cdr: ChangeDetectorRef,
    private loginDetailsService: LoginDetailsService,
    private loaderService: LoaderService
  ) {
    this.initateFormSetup();
  }

  ngOnInit(): void {
    this.initateFormSetup();
  }

  initateFormSetup() {
    this.registrationForm = this.fb.group(
      {
        email: [
          '',
          [
            Validators.required,
            Validators.pattern(
              '^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+.[A-Za-z]{2,}$'
            ),
            SignupComponent.emailDomainValidator(),
          ],
        ],
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            this.validatePassword,
            Validators.pattern(/^\S*$/),
          ],
        ],
        confirmPassword: [
          '',
          [Validators.required, Validators.pattern(/^\S*$/)],
        ],
      },
      { validators: this.passwordMatchValidator }
    );

    this.otpForm = this.fb.group({
      otp1: this.fb.control('', [Validators.required, Validators.maxLength(1)]),
      otp2: this.fb.control('', [Validators.required, Validators.maxLength(1)]),
      otp3: this.fb.control('', [Validators.required, Validators.maxLength(1)]),
      otp4: this.fb.control('', [Validators.required, Validators.maxLength(1)]),
      otp5: this.fb.control('', [Validators.required, Validators.maxLength(1)]),
      otp6: this.fb.control('', [Validators.required, Validators.maxLength(1)]),
    });

    this.mfaMobileForm = this.fb.group({
      countryCode: ['', [Validators.required]],
      mobileNumber: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(10), Validators.pattern(/^[0-9]+$/)]]
    });

    this.mfaOtpForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/), Validators.minLength(6), Validators.maxLength(6)]]
    });

    this.mfaTotpForm = this.fb.group({
      totpCode: ['', [Validators.required, Validators.pattern(/^[0-9]{6}$/)]]
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    this.emailAddress = simpleChanges?.emailAddress?.currentValue;
    if (this.emailAddress) {
      this.redirectToLogin = true;
      this.sendOtp();
    }
    if (simpleChanges?.showMfaSetupDirectly?.currentValue === true) {
      this.showSignUpScreen = false;
      this.showOTPScreen = false;
      this.showMfaSetupScreen = true;
    }
    this.cdr.detectChanges();
  }

  moveToNext(
    event: KeyboardEvent,
    nextField: HTMLInputElement,
    prevField: HTMLInputElement
  ): void {
    const input = event.target as HTMLInputElement;

    // Move to the next field if a character is entered
    if (input.value.length === 1 && nextField) {
      nextField.focus();
    }

    // Move to the previous field if backspace is pressed and the current field is empty
    if (event.key === 'Backspace' && input.value.length === 0 && prevField) {
      prevField.focus();
    }
  }

  static emailDomainValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const email = control.value;
      if (email) {
        if (!emailPattern.test(email)) {
          return { domainInvalid: true };
        }
      }
      return null;
    };
  }
  passwordMatchValidator(group: FormGroup): any {
    const password = group.get('password')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  validatePassword(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return { required: true };
    }
    if (value.length < 8) {
      return { minlength: { requiredLength: 8, actualLength: value.length } };
    }
    const hasUpperCase = /[A-Z]/.test(value);
    const hasSpecialChar = /[^a-zA-Z0-9\s]/.test(value);
    const hasNumber = /\d/.test(value);

    if (!hasUpperCase || !hasSpecialChar || !hasNumber) {
      return { passwordStrength: true };
    }
    return null;
  }

  signin() {
    this.goToSignin.emit();
  }

  logOut() {
    this.showOTPScreen = false;
    this.showSignUpScreen = true;
    this.goToSignin.emit();
    // this.authService.logoutSignedInUser();
  }

  sendOtp(): void {
    this.emailAddress = this.email?.value;

    if (
      !this.email?.value ||
      !this.password?.value ||
      !this.confirmPassword?.value
    ) {
      this.registrationForm.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    if (!this.registrationForm.valid) {
      this.registrationForm.markAllAsTouched();
      this.cdr.detectChanges();
      return;
    }

    const email = this.registrationForm.get('email')?.value;
    const password = this.registrationForm.get('password')?.value;

    this.authService.sendOtp(email, password).subscribe(
      (response) => {
        this.showOTPScreen = true;
        this.showSignUpScreen = false;
        this.loginDetailsService.setPlayerUsername(email);
        this.toast.toastSuccess('MODULES.LOGIN.LOGIN_OTP');
        setTimeout(() => {
          this.showOTPScreen = true;
          this.cdr.detectChanges();
        }, 0);
      },
      (error) => {
      }
    );
  }

  async resendOtp() {
    const email = this.email?.value
      ? this.email?.value
      : this.emailAddress
      ? this.emailAddress
      : this.emailEntered;
    const password = this.registrationForm.get('password')?.value
      ? this.registrationForm.get('password')?.value
      : this.passwordEntered;
    await this.authService.resendSignUpCode(email, password);
    this.loaderService.setLoaderStateActive(false, 'cognito_service_url');
    this.toast.toastSuccess('MODULES.LOGIN.LOGIN_OTP');
  }

  async verifyOtpSignUp() {
    if (!this.otpForm) {
      this.otpForm.markAllAsTouched();
      this.cdr.detectChanges();
      this.toast.toastError('MODULES.LOGIN.OTP_FORM');
      return;
    }

    const otp1 = this.otpForm.controls['otp1']?.value;
    const otp2 = this.otpForm.controls['otp2']?.value;
    const otp3 = this.otpForm.controls['otp3']?.value;
    const otp4 = this.otpForm.controls['otp4']?.value;
    const otp5 = this.otpForm.controls['otp5']?.value;
    const otp6 = this.otpForm.controls['otp6']?.value;
    if (!otp1 || !otp2 || !otp3 || !otp4 || !otp5 || !otp6) {
      this.toast.toastError('Please enter the full OTP');
      return;
    }
    this.otpCode = `${otp1}${otp2}${otp3}${otp4}${otp5}${otp6}`;
    const email = this.email?.value
      ? this.email?.value
      : this.emailAddress
      ? this.emailAddress
      : this.emailEntered;
    const password = this.registrationForm.get('password')?.value
      ? this.registrationForm.get('password')?.value
      : this.passwordEntered;

    if (!this.otpCode || !email) {
      return;
    }

    try {
      const data = await this.authService.verifyOtpSignUp(email, this.otpCode);

      if (!this.skipAutoLogin) {
        const signInResponse = await this.authService.signIn(email, password);
        if (!signInResponse?.success) {
          // OTP verified but sign-in failed (e.g. wrong password) — stay on OTP screen
          return;
        }
      }

      if (this.loginDetailsService.showMfaSetupOnRegistration) {
        this.showOTPScreen = false;
        this.showSignUpScreen = false;
        this.showMfaSetupScreen = true;
        this.cdr.detectChanges();
      } else {
        this.onSignupCompleted.emit(new SignUpStatus(true, email));
        this.toast.toastSuccess('MODULES.LOGIN.REGISTER_SUCCESSFULL');
      }
    } catch (error) {}
  }

  displayRegistrationPage() {
    this.registrationForm.reset();
    if (this.redirectToLogin) {
      this.goToSignin.emit();
    } else {
      this.showOTPScreen = false;
      this.showSignUpScreen = true;
    }
  }

  // ─── MFA Setup (shown after registration OTP verification) ───────────────

  mfaStartMobileSetup(): void {
    this.showMfaMobileScreen = true;
  }

  async mfaStartAuthenticatorSetup(): Promise<void> {
    try {
      const totpDetails = await this.authService.setupAuthenticatorTOTP();
      this.mfaTotpSecret = totpDetails.sharedSecret;
      this.showMfaAuthenticatorScreen = true;
    } catch (error) {
      this.toast.toastError('MODULES.ACCOUNT_SECURITY.MFA.MFA_ERROR');
    }
  }

  async mfaSubmitMobile(): Promise<void> {
    if (!this.mfaMobileForm.valid) {
      this.mfaMobileForm.markAllAsTouched();
      return;
    }
    try {
      const countryCode: string = this.mfaMobileForm.get('countryCode')?.value ?? '';
      const mobileNumber: string = this.mfaMobileForm.get('mobileNumber')?.value ?? '';
      await this.authService.updatePhoneNumber(`${countryCode}${mobileNumber}`);
      await this.authService.sendPhoneVerificationCode();
      this.showMfaMobileScreen = false;
      this.showMfaMobileOTPScreen = true;
    } catch (error) {
      this.toast.toastError('MODULES.ACCOUNT_SECURITY.MFA.MFA_ERROR');
    }
  }

  async mfaSubmitMobileOTP(): Promise<void> {
    if (!this.mfaOtpForm.valid) {
      this.mfaOtpForm.markAllAsTouched();
      return;
    }
    const code: string = this.mfaOtpForm.get('otp')?.value;
    try {
      await this.authService.confirmPhoneVerification(code);
      await this.authService.updatePlayerMfaPreference(true, 'SMS');
      this.toast.toastSuccess('MODULES.ACCOUNT_SECURITY.MFA.MFA_SUCCESS');
      this._completeMfaSetup();
    } catch (error) {
      this.toast.toastError('MODULES.ACCOUNT_SECURITY.MFA.OTP_VERIFY_ERROR');
    }
  }

  async mfaSubmitTOTP(): Promise<void> {
    if (!this.mfaTotpForm.valid) {
      this.mfaTotpForm.markAllAsTouched();
      return;
    }
    const code: string = this.mfaTotpForm.get('totpCode')?.value;
    try {
      await this.authService.verifyAuthenticatorTOTP(code);
      await this.authService.updatePlayerMfaPreference(true, 'TOTP');
      this.toast.toastSuccess('MODULES.ACCOUNT_SECURITY.MFA.AUTHENTICATOR_SUCCESS');
      this._completeMfaSetup();
    } catch (error) {
      this.toast.toastError('MODULES.ACCOUNT_SECURITY.MFA.TOTP_VERIFY_ERROR');
    }
  }

  mfaCancelMobileOTPBack(): void {
    this.showMfaMobileOTPScreen = false;
    this.showMfaMobileScreen = true;
  }

  async mfaResendMobileOTP(): Promise<void> {
    try {
      await this.authService.sendPhoneVerificationCode();
      this.mfaOtpForm.reset();
      this.toast.toastSuccess('MODULES.ACCOUNT_SECURITY.MFA.OTP_RESENT');
    } catch (error) {
      this.toast.toastError('MODULES.ACCOUNT_SECURITY.MFA.MFA_ERROR');
    }
  }

  mfaCancelMobileBack(): void {
    this.showMfaMobileScreen = false;
    this.showMfaSetupScreen = true;
  }

  mfaCancelAuthenticatorBack(): void {
    this.showMfaAuthenticatorScreen = false;
    this.mfaTotpSecret = '';
    this.mfaTotpForm.reset();
    this.showMfaSetupScreen = true;
  }

  mfaSkip(): void {
    this._completeMfaSetup();
  }

  private _completeMfaSetup(): void {
    const email = this.email?.value
      ? this.email?.value
      : this.emailAddress
      ? this.emailAddress
      : this.emailEntered;
    this.showMfaSetupScreen = false;
    this.showMfaMobileScreen = false;
    this.showMfaMobileOTPScreen = false;
    this.showMfaAuthenticatorScreen = false;
    this.onSignupCompleted.emit(new SignUpStatus(true, email));
    // this.toast.toastSuccess('MODULES.LOGIN.REGISTER_SUCCESSFULL');
  }

  get mfaMaskedMobileNumber(): string {
    const mobile: string = this.mfaMobileForm?.get('mobileNumber')?.value ?? '';
    if (!mobile) return '';
    return mobile.slice(0, -4).replace(/./g, '*') + mobile.slice(-4);
  }

  mfaCopySecret(): void {
    if (this.mfaTotpSecret) {
      navigator.clipboard.writeText(this.mfaTotpSecret);
      this.toast.toastSuccess('MODULES.ACCOUNT_SECURITY.MFA.SECRET_COPIED');
    }
  }
}
